import axios from 'axios';
import { AuthClient } from '../authClient';
import { nanoid } from 'nanoid';
import { RepositoryInstanceConfig } from 'api/base';
import { FEHeadersCookiesUtil, FELocalStorageUtil } from 'utils';
import moment from 'moment';
import { Language } from 'models';
import md5 from 'md5';
import { INITIAL_LANGUAGE } from 'const';

export type AcceptLanguage = 'ru-RU' | 'en-AE' | 'hy-AM' | 'kk-KZ';
export interface Headers {
  'X-Device-Type'?: string;
  'X-Auth-Token'?: string;
  'X-Device-Token'?: string;
  'X-Date'?: string;
  'X-Version'?: string;
  'X-Customer'?: string;
  'X-City-Id'?: string;
  'Accept-Language'?: AcceptLanguage;
  'X-UTM-Source'?: string | null;
  'X-UTM-Medium'?: string | null;
  'X-UTM-CAMPAIGN'?: string | null;
}

const getAcceptLanguage: Record<Language, AcceptLanguage> = {
  ru: 'ru-RU',
  en: 'en-AE',
  hy: 'hy-AM',
  kz: 'kk-KZ',
};

const canUseBrowserGlobals = () =>
  typeof window !== 'undefined' && typeof localStorage !== 'undefined';

export const getConfirmHeadersValues = () => {
  const code = FEHeadersCookiesUtil.getCode;
  const sign = FEHeadersCookiesUtil.getSign;
  const date =
    moment()
      .locale('en')
      .utc(true)
      .utcOffset(new Date().getTimezoneOffset())
      .format('ddd, D MMM YYYY HH:mm:ss') + ' GMT';

  return {
    code,
    date,
    sign,
  };
};

const getHeaders = () => {
  const utm = FEHeadersCookiesUtil.getUTMParams();
  const urlParams = new URLSearchParams(
    typeof window !== 'undefined' ? window.location.search : '',
  );

  const utmSource = urlParams.get('utm_source');
  const utmMedium = urlParams.get('utm_medium');
  const utmCampaign = urlParams.get('utm_campaign');

  const headers: Headers = {
    'X-Device-Type': 'WEB',
  };
  const token = AuthClient.tokens.accessToken;
  if (token) {
    headers['X-Auth-Token'] = token;
  }
  //TODO: вот тут плохо используется localStorage
  if (canUseBrowserGlobals() && localStorage.getItem('deviceToken')) {
    headers['X-Device-Token'] = localStorage.getItem('deviceToken') as string;
  } else {
    const deviceToken = nanoid();
    headers['X-Device-Token'] = deviceToken;
  }
  if (utm.utm_source || utmSource) {
    headers['X-UTM-Source'] = utm.utm_source ? utm.utm_source : utmSource;
  }
  if (utm.utm_medium || utmMedium) {
    headers['X-UTM-Medium'] = utm.utm_medium ? utm.utm_medium : utmMedium;
  }
  if (utm.utm_campaign || utmCampaign) {  
    headers['X-UTM-CAMPAIGN'] = utm.utm_campaign ? utm.utm_campaign : utmCampaign;
  }
  return { common: { ...headers } };
};

export const axiosRepository = ({
  baseUrl,
  timeout,
  tokenRefresh,
  authErrors = {
    reasonKey: 'reason',
    tokenExpired: ['ACCESS_TOKEN_EXPIRED'],
    permissionInsufficient: ['CREDENTIAL_PERMISSION_INSUFFICIENT'],
    needToLogin: [
      'CREDENTIAL_BLOCKED',
      'ACCESS_TOKEN_INACTIVATED',
      'ACCESS_TOKEN_NOT_EXIST',
      'ACCESS_TOKEN_REFRESHED',
      'ACCESS_TOKEN_MODIFIED',
      'ACCESS_TOKEN_MALFORMED',
    ],
  },
}: RepositoryInstanceConfig) => {
  const headers = getHeaders();
  const repository = axios.create({
    // @ts-ignore
    headers,
    baseURL: baseUrl,
    timeout: timeout,
  });
  repository.defaults.timeout = 30000;
  repository.interceptors.request.use(
    (request) => {
      const lng = FEHeadersCookiesUtil.getLanguage || INITIAL_LANGUAGE;
      if (lng) {
        // @ts-ignore
        request.headers.common['Accept-Language'] = getAcceptLanguage[lng];
      }
      const { city } = FELocalStorageUtil.getCartography;
      if (city) {
        // @ts-ignore
        request.headers.common['X-City-Id'] = city.id;
      }
      const { code, date, sign } = getConfirmHeadersValues();

      const phone = FEHeadersCookiesUtil.getPhone;
      if (phone) {
        // @ts-ignore
        request.headers.common['X-Customer'] = phone;
      }

      // @ts-ignore
      request.headers.common['X-Date'] = date;
      if (code) {
        // @ts-ignore
        request.headers.common['X-Version'] = md5(code + date);
      }
      if (sign) {
        // @ts-ignore
        request.headers.common['X-Sign'] = sign;
      }
      const token = AuthClient.tokens.accessToken;
      if (token) {
        // @ts-ignore
        request.headers.common['X-Auth-Token'] = token;
      }
      return request;
    },
    (error) => error,
  );

  repository.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      const reason =
        error.response?.data?.errors[0]?.[authErrors.reasonKey || 'reason'];
      if (authErrors.tokenExpired.includes(reason)) {
        let response;
        try {
          response = (await tokenRefresh()).data.result;
          if ('accessToken' in response === false) throw response;
        } catch (err) {
          AuthClient.clearCookies();
          return Promise.reject();
        }
        const config = error.config;
        config.headers['X-Auth-Token'] = response.accessToken;
        return await repository.request(config);
      } else if (authErrors.permissionInsufficient.includes(reason)) {
        return Promise.reject();
      } else if (authErrors.needToLogin.includes(reason)) {
        AuthClient.clearCookies();
        return Promise.reject();
      }

      return Promise.reject(error);
    },
  );
  return repository;
};
